import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { PosthogService } from './posthog.service';
import { UtmTrackingService } from './utm-tracking.service';

@Injectable({
  providedIn: 'root',
})
export class PageTrackingService {
  // Time tracking
  private pageEntryTime: number = 0;
  private currentPageName: string = '';
  private timeTrackingInterval: any;
  private trackedTimeMilestones: Set<number> = new Set();
  private readonly TIME_MILESTONES = [30, 60, 90, 120, 150, 180, 210, 240, 270, 300]; // 30s intervals up to 5 mins

  // Scroll depth tracking
  private trackedScrollMilestones: Set<number> = new Set();
  private readonly SCROLL_MILESTONES = [25, 50, 75, 100];
  private scrollListener: (() => void) | null = null;
  private lastScrollCheck: number = 0;
  private readonly SCROLL_THROTTLE_MS = 200; // Check scroll every 200ms max

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private router: Router,
    private posthog: PosthogService,
    private utmTrackingService: UtmTrackingService
  ) {
    if (isPlatformBrowser(this.platformId)) {
      this.initializeTracking();
    }
  }

  private initializeTracking(): void {
    // Start tracking for initial page load immediately
    if (typeof window !== 'undefined') {
      const initialPageName = this.extractPageName(window.location.pathname);
      this.startTracking(initialPageName);
    }

    // Listen to all route navigation events for subsequent navigations
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        // Stop tracking for previous page
        this.stopTracking();

        // Start tracking for new page
        const pageName = this.extractPageName(event.urlAfterRedirects);
        this.startTracking(pageName);
      });

    // Track when user leaves the site (browser close/tab close)
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => {
        this.stopTracking();
      });
    }
  }

  private startTracking(pageName: string): void {
    this.currentPageName = pageName;
    this.pageEntryTime = Date.now();
    this.trackedTimeMilestones.clear();
    this.trackedScrollMilestones.clear();

    // Increment page view count for session tracking
    this.utmTrackingService.incrementPageView();

    // Start time on page tracking
    this.timeTrackingInterval = setInterval(() => {
      this.checkTimeMilestones();
    }, 1000); // Check every 1 second

    // Start scroll depth tracking
    this.setupScrollTracking();
  }

  private stopTracking(): void {
    // Clear time tracking interval
    if (this.timeTrackingInterval) {
      clearInterval(this.timeTrackingInterval);
      this.timeTrackingInterval = null;
    }

    // Remove scroll listener
    if (this.scrollListener && typeof window !== 'undefined') {
      window.removeEventListener('scroll', this.scrollListener, { passive: true } as any);
      this.scrollListener = null;
    }
  }

  private checkTimeMilestones(): void {
    const currentTime = Date.now();
    const elapsedSeconds = Math.floor((currentTime - this.pageEntryTime) / 1000);

    // Check which milestone we've reached
    for (const milestone of this.TIME_MILESTONES) {
      // If we've passed this milestone and haven't tracked it yet
      if (
        elapsedSeconds >= milestone &&
        !this.trackedTimeMilestones.has(milestone)
      ) {
        this.trackedTimeMilestones.add(milestone);

        // Fire event: time_on_page_30s, time_on_page_60s, etc.
        this.posthog.capture(`time_on_page_${milestone}s`, {
          page_name: this.currentPageName,
          duration_seconds: milestone,
          timestamp: new Date().toISOString(),
          page_url:
            typeof window !== 'undefined' ? window.location.href : '',
        });
      }
    }

    // Stop tracking after 5 minutes (300 seconds)
    if (elapsedSeconds >= 300 && this.timeTrackingInterval) {
      clearInterval(this.timeTrackingInterval);
      this.timeTrackingInterval = null;
    }
  }

  private setupScrollTracking(): void {
    if (typeof window === 'undefined') return;

    // Throttled scroll handler
    this.scrollListener = () => {
      const now = Date.now();
      if (now - this.lastScrollCheck < this.SCROLL_THROTTLE_MS) {
        return; // Throttle: don't check too frequently
      }
      this.lastScrollCheck = now;

      this.checkScrollMilestones();
    };

    // Add passive scroll listener for better performance
    window.addEventListener('scroll', this.scrollListener, { passive: true } as any);

    // Check immediately in case page loads scrolled down
    this.checkScrollMilestones();
  }

  private checkScrollMilestones(): void {
    if (typeof window === 'undefined' || typeof document === 'undefined')
      return;

    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    const scrollTop =
      window.pageYOffset || document.documentElement.scrollTop;

    // Calculate scroll percentage
    const scrollableHeight = documentHeight - windowHeight;
    if (scrollableHeight <= 0) {
      // Page is not scrollable, mark 100% immediately
      if (!this.trackedScrollMilestones.has(100)) {
        this.trackedScrollMilestones.add(100);
        this.posthog.capture('scroll_depth_100_reached', {
          page_name: this.currentPageName,
          depth_percentage: 100,
          page_url: window.location.href,
          timestamp: new Date().toISOString(),
        });
      }
      return;
    }

    const scrollPercentage = (scrollTop / scrollableHeight) * 100;

    // Check which milestones have been reached
    for (const milestone of this.SCROLL_MILESTONES) {
      // For 100% milestone, using 98% threshold to account for browser rounding/fractional pixels
      const threshold = milestone === 100 ? 98 : milestone;
      
      if (
        scrollPercentage >= threshold &&
        !this.trackedScrollMilestones.has(milestone)
      ) {
        this.trackedScrollMilestones.add(milestone);

        // Fire event: scroll_depth_25_reached, scroll_depth_50_reached, etc.
        this.posthog.capture(`scroll_depth_${milestone}_reached`, {
          page_name: this.currentPageName,
          depth_percentage: milestone,
          page_url: window.location.href,
          timestamp: new Date().toISOString(),
        });
      }
    }
  }

  private extractPageName(url: string): string {
    // Remove query params and hash
    const cleanUrl = url.split('?')[0].split('#')[0];
    // Get last segment
    const segments = cleanUrl.split('/').filter(s => s);
    const pageName = segments[segments.length - 1] || 'home';
    // Normalize to snake_case
    return pageName.toLowerCase().replace(/-/g, '_');
  }
}
